import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useRef, useState, useEffect, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

// Lazy load the map component to defer Leaflet (~41KB) until needed
const ServiceAreaMap = lazy(() => import("@/components/ServiceAreaMap"));

// Service areas with links for SEO accessibility
const serviceAreaLinks = [
  { name: "The Woodlands", href: "/roofing-the-woodlands-tx" },
  { name: "Kingwood", href: "/roofing-kingwood-tx" },
  { name: "Humble", href: "/roofing-humble-tx" },
  { name: "Atascocita", href: "/roofing-atascocita-tx" },
  { name: "Spring", href: "/roofing-spring-tx" },
  { name: "Conroe", href: "/roofing-conroe-tx" },
  { name: "Aldine", href: "/roofing-aldine-tx" },
];

const LocalAuthoritySection = () => {
  const { t } = useTranslation('home');
  const [mapVisible, setMapVisible] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mapObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setMapVisible(true);
      },
      { threshold: 0.1 }
    );
    if (mapRef.current) mapObserver.observe(mapRef.current);
    return () => {
      mapObserver.disconnect();
    };
  }, []);

  return (
    <section className="bg-[#1a2744] relative py-28 md:py-36 overflow-hidden">
      {/* Map Background - more visible */}
      <div ref={mapRef} className="absolute inset-0 opacity-50">
        {mapVisible && (
          <Suspense
            fallback={
              <div className="w-full h-full bg-slate-800 animate-pulse" />
            }
          >
            <ServiceAreaMap />
          </Suspense>
        )}
      </div>

      {/* Subtle gradient overlay - less opaque to show map */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a2744]/40 via-transparent to-[#1a2744]/60" />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
        {/* Simple Pin Icon - like reference */}
        <motion.div
          className="inline-flex items-center justify-center w-20 h-20 bg-secondary rounded-full mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <MapPin className="w-8 h-8 text-primary" />
        </motion.div>

        {/* Large Headline */}
        <motion.h2
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-black uppercase text-white mb-10 tracking-tight"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          {t('localAuthority.headlinePart1')} <br />
          <span className="text-primary">{t('localAuthority.headlinePart2')}</span>
        </motion.h2>

        {/* Location Pills - simple outline style like reference */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {serviceAreaLinks.map((area, index) => (
            <Link
              key={index}
              to={area.href}
              className="px-5 py-2.5 rounded-full text-sm font-medium text-white/90 border border-white/30 hover:bg-white/10 hover:border-white/50 transition-all cursor-pointer"
            >
              {area.name}
            </Link>
          ))}
        </motion.div>

        {/* Subtext */}
        <motion.p
          className="text-base font-medium text-white/60"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {t('localAuthority.subtext')}
        </motion.p>
      </div>
    </section>
  );
};

export default LocalAuthoritySection;
